// src/screens/TimePicker.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/** Props */
type TimePickerProps = {
  initialTime?: string;              // e.g. "4:30 PM" or "9:00am"
  onConfirm: (time: string) => void; // returns "HH:MM AM/PM"
  onClose: () => void;
  minuteStep?: number;               // default 30
};

const ITEM_HEIGHT = 32;       // height of each row
const VIEWPORT_HEIGHT = 160;  // shows 5 rows
const PAD = (VIEWPORT_HEIGHT - ITEM_HEIGHT) / 2; // 64px top/bottom padding

/* ------------------------------- utilities -------------------------------- */

function parseInitialTime(str?: string) {
  const fallback = { h: 4, m: 30, p: "PM" as "AM" | "PM" };
  if (!str) return fallback;

  const s = str.trim().toLowerCase();
  const m =
    s.match(/^(\d{1,2})\s*:\s*(\d{1,2})\s*(am|pm)$/i) ||
    s.match(/^(\d{1,2})\s*:\s*(\d{1,2})(am|pm)$/i);
  if (!m) return fallback;

  const hh = Math.max(1, Math.min(12, parseInt(m[1], 10)));
  const mm = Math.max(0, Math.min(59, parseInt(m[2], 10)));
  const p = m[3].toUpperCase() as "AM" | "PM";
  return { h: hh, m: mm, p };
}
const pad2 = (n: number) => n.toString().padStart(2, "0");

/* ------------------------------ WheelColumn ------------------------------- */

type WheelColumnProps<T extends string | number> = {
  values: ReadonlyArray<T>;
  value: T;
  onChange: (v: T) => void;
  formatter?: (v: T) => string;
  ariaLabel?: string;
};

function WheelColumn<T extends string | number>({
  values,
  value,
  onChange,
  formatter = (v) => String(v),
  ariaLabel,
}: WheelColumnProps<T>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const snapTimer = useRef<number | null>(null);

  const scrollToIndex = (i: number, behavior: ScrollBehavior = "auto") => {
    if (!ref.current) return;
    ref.current.scrollTo({ top: i * ITEM_HEIGHT, behavior });
  };

  const indexOfValue = (val: T) =>
    Math.max(0, values.findIndex((v) => v === val));

  // Align to current value on mount and when value changes programmatically
  useEffect(() => {
    scrollToIndex(indexOfValue(value), "auto");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, values.length]);

  const snapToNearest = (smooth = true) => {
    if (!ref.current) return;
    const i = Math.round(ref.current.scrollTop / ITEM_HEIGHT);
    const idx = Math.max(0, Math.min(values.length - 1, i));
    const next = values[idx];
    scrollToIndex(idx, smooth ? "smooth" : "auto");
    if (next !== value) onChange(next);
  };

  const scheduleSnap = () => {
    if (snapTimer.current) window.clearTimeout(snapTimer.current);
    snapTimer.current = window.setTimeout(() => {
      snapToNearest(true);
      snapTimer.current = null;
    }, 120);
  };

  useEffect(() => {
    return () => {
      if (snapTimer.current) window.clearTimeout(snapTimer.current);
    };
  }, []);

  return (
    <div className="w-[72px]">
      <div
        ref={ref}
        role="listbox"
        aria-label={ariaLabel}
        className="overflow-y-auto scroll-smooth snap-y snap-mandatory [&::-webkit-scrollbar]:hidden"
        style={{ 
          height: VIEWPORT_HEIGHT,
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
        }}
        onScroll={scheduleSnap}
        onMouseUp={() => snapToNearest(true)}
        onTouchEnd={() => snapToNearest(true)}
      >
        {/* spacer so first/last can sit centered */}
        <div style={{ paddingTop: PAD, paddingBottom: PAD }}>
          {values.map((v, index) => {
            const active = v === value;
            const activeIndex = values.indexOf(value);
            const distance = Math.abs(index - activeIndex);
            
            // Calculate opacity based on distance from selected item
            let opacity = 1;
            if (distance === 1) opacity = 0.6;
            else if (distance === 2) opacity = 0.3;
            else if (distance >= 3) opacity = 0.1;
            
            return (
              <div
                key={String(v)}
                role="option"
                aria-selected={active}
                className={[
                  "h-[32px] grid place-items-center cursor-pointer select-none snap-center",
                  active
                    ? "text-[#D62422] font-extrabold" // selected in RED
                    : "text-[#8A8A8A]",
                ].join(" ")}
                style={{ opacity: active ? 1 : opacity }}
                onClick={() => {
                  onChange(v);
                  const i = values.indexOf(v);
                  if (i >= 0) scrollToIndex(i, "smooth");
                }}
              >
                <span className="text-[18px] leading-none">
                  {formatter(v)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- Component -------------------------------- */

const TimePicker: React.FC<TimePickerProps> = ({
  initialTime = "4:30 PM",
  onConfirm,
  onClose,
  minuteStep = 30,
}) => {
  const init = parseInitialTime(initialTime);

  const [hour, setHour] = useState<number>(init.h);
  const [minute, setMinute] = useState<number>(
    Math.round(init.m / minuteStep) * minuteStep
  );
  const [period, setPeriod] = useState<"AM" | "PM">(init.p);

  // Update state when initialTime prop changes
  useEffect(() => {
    const newInit = parseInitialTime(initialTime);
    setHour(newInit.h);
    setMinute(Math.round(newInit.m / minuteStep) * minuteStep);
    setPeriod(newInit.p);
  }, [initialTime, minuteStep]);

  const hours = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const minutes = useMemo(
    () =>
      Array.from(
        { length: Math.floor(60 / minuteStep) },
        (_, i) => i * minuteStep
      ),
    [minuteStep]
  );
  const periods = useMemo(() => ["AM", "PM"] as const, []);

  const confirm = () => onConfirm(`${pad2(hour)}:${pad2(minute)} ${period}`);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
      <div className="w-full max-w-sm bg-[#F5F5F5] overflow-hidden">
        {/* Wheels: single center border spans all columns; wheels auto-snap to its center */}
        <div className="px-5 pt-8 pb-6 flex justify-center">
          <div
            className="relative w-full max-w-[320px] bg-transparent overflow-hidden"
            style={{ height: VIEWPORT_HEIGHT }}
          >
            {/* one bordered rectangle around the selected row */}
            <div
              className="pointer-events-none absolute left-0 right-0 border border-[#CFCFCF]"
              style={{
                top: (VIEWPORT_HEIGHT - ITEM_HEIGHT) / 2,
                height: ITEM_HEIGHT,
              }}
            />


            {/* wheels + static separators aligned to the same center row */}
            <div className="relative h-full flex items-stretch justify-center gap-3 px-2">
              <WheelColumn
                values={hours}
                value={hour}
                onChange={setHour}
                formatter={(v) => pad2(v as number)}
                ariaLabel="Hours"
              />

              <div className="w-4 h-full grid place-items-center">
                <span className="text-[18px] font-extrabold text-[#2D2D2D]">
                  :
                </span>
              </div>

              <WheelColumn
                values={minutes}
                value={minute}
                onChange={setMinute}
                formatter={(v) => pad2(v as number)}
                ariaLabel="Minutes"
              />

              <div className="w-4 h-full grid place-items-center">
                <span className="text-[18px] font-extrabold text-[#2D2D2D]">
                  :
                </span>
              </div>

              <WheelColumn
                values={periods}
                value={period}
                onChange={(v) => setPeriod(v as "AM" | "PM")}
                formatter={(v) => String(v)}
                ariaLabel="AM or PM"
              />
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between px-5 pb-5">
          <button
            onClick={onClose}
            className="w-12 h-12 bg-white border border-[#CFCFCF] grid place-items-center"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M6 18L18 6M6 6l12 12"
                stroke="#6B6B6B"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <button
            onClick={confirm}
            className="ml-4 h-12 flex-1 bg-[#D62422] hover:brightness-95 active:brightness-90 text-white font-extrabold tracking-wide grid grid-cols-[auto_1fr_auto] items-center px-5"
          >
            <span className="justify-self-start">CONFIRM</span>
            <span className="justify-self-end">
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimePicker;

/* ----------------------------- Tailwind helpers ----------------------------
Add once globally to fully hide scrollbars:

.no-scrollbar::-webkit-scrollbar { display: none; }          /* Chrome/Safari */
// .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; } /* FF/Edge */
// ---------------------------------------------------------------------------- */
